import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import {
  AUTH_SESSION_ENDED_EVENT,
  AuthenticationProvider,
  PermissionsProvider,
  useAuthentication,
} from '@src/features/auth';
import { mapBackendLanguageToApp, useAPIClient } from '@src/shared';
import { Composer, ErrorFallback } from '@src/shared/lib';
import { SaveShortcutProvider } from '@src/shared/ui/SaveShortcutScope';
import { MuiDateProvider } from '@src/shared/ui/providers';
import { QueryClientProvider } from '@tanstack/react-query';
import { CatchBoundary, RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Devtools } from './devtools';
// Registers where each section's task references open, before anything renders.
import './taskReferences';
import { NotiFicationDialogProvider } from './notifications/dialog';
import { ToastProvider } from './notifications/toast';
import { queryClient } from './providers';
import router from './router';
import { theme } from './themes';
import { VersionWatcher } from './version';
import './styles';
import './fonts';

function InnerApp() {
  const auth = useAuthentication();
  const apiClient = useAPIClient();

  useEffect(() => {
    let isLogoutPending = false;

    const navigateToLogin = () => {
      isLogoutPending = false;
      void router.navigate({ to: '/auth/login', replace: true }).then(() => {
        queryClient.clear();
      });
    };
    const handleSessionEnded = () => {
      if (document.visibilityState === 'hidden') {
        isLogoutPending = true;
        return;
      }
      navigateToLogin();
    };
    const handleVisibilityChange = () => {
      if (
        isLogoutPending &&
        document.visibilityState === 'visible'
      ) {
        navigateToLogin();
      }
    };

    window.addEventListener(
      AUTH_SESSION_ENDED_EVENT,
      handleSessionEnded,
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener(
        AUTH_SESSION_ENDED_EVENT,
        handleSessionEnded,
      );
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      );
    };
  }, []);

  return <RouterProvider router={router} context={{ auth, apiClient }} />;
}

function AuthLanguageSync() {
  const auth = useAuthentication();
  const { i18n } = useTranslation();

  useEffect(() => {
    const nextLanguage = mapBackendLanguageToApp(auth.user?.language);
    if (nextLanguage !== i18n.resolvedLanguage) {
      void i18n.changeLanguage(nextLanguage);
    }
  }, [auth.user?.language, i18n]);

  return null;
}

function App() {
  const providers = useMemo(
    () => [
      { element: ThemeProvider, props: { theme: theme } },
      { element: StyledEngineProvider, props: { injectFirst: true } },
      { element: MuiDateProvider, props: {} },
      { element: AuthenticationProvider, props: {} },
      { element: PermissionsProvider, props: {} },
      { element: QueryClientProvider, props: { client: queryClient } },
      { element: ToastProvider, props: {} },
      { element: NotiFicationDialogProvider, props: {} },
    ],
    [],
  );

  return (
    <SaveShortcutProvider>
      <CatchBoundary getResetKey={() => 'reset'} errorComponent={ErrorFallback}>
        <Composer providers={providers}>
          <CssBaseline />
          <AuthLanguageSync />
          <VersionWatcher />
          <InnerApp />
          <Devtools router={router} />
        </Composer>
      </CatchBoundary>
    </SaveShortcutProvider>
  );
}

export default App;
